import { useNavigate } from "@tanstack/react-router";
import {
  ArchiveX,
  ArrowRight,
  Ban,
  BellRing,
  ChevronDown,
  Download,
  ExternalLink,
  FileSearch,
  Files,
  FileText,
  FolderOpen,
  type LucideIcon,
  Mail,
  MessagesSquare,
  Plane,
  RotateCw,
  Scale,
  Send,
  SlidersHorizontal,
  SquarePen,
  Ticket,
  Trash2,
} from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrentUser } from "@/hooks/use-current-user";
import { config } from "@/lib/config";
import { notify } from "@/lib/notify";
import type { PermissionRequirement } from "@/lib/permissions";

import type { ClaimWithRelations } from "../api/use-claim";
import {
  useCreateOnePager,
  useGenerateFormA,
  useRegenerateFirstEmail,
  useRequestClaimDocument,
  useResetClaimTimeline,
  useSyncReservationTickets,
} from "../api/use-claim-actions";
import { usePullClaimRealDocuments } from "../api/use-claim-mutations";
import type { ClaimLegalStepName } from "../lib/legal-steps";
import { isBoxFolderAvailable, openBoxFolder } from "../lib/open-box";
import { openTribunalDigital } from "../lib/open-tribunal-digital";
import { useClaimMachine } from "../machine/use-claim-machine";
import { BlockedFlagModal } from "./blocked-flag-modal";
import { BookingCertificateDialog } from "./booking-certificate-dialog";
import { CreateClaimTaskModal } from "./create-claim-task-modal";
import { ConversationSelectionModal } from "./emails/conversation-selection-modal";
import { InitIncidentModal } from "./incidents/init-incident-modal";
import { MediationFormArchiveModal } from "./mediation-form-archive-modal";
import { SelectAbandonType } from "./select-abandon-type";
import { SendEventModal } from "./send-event-modal";
import { GeneratePleadingBriefCTA } from "./states-cta/generate-pleading-brief-cta";
import { GenerateStampedExhibitsCTA } from "./states-cta/generate-stamped-exhibits-cta";
import { TravelAgencySignatureCTA } from "./states-cta/travel-agency-signature-cta";
import { UpdateClaimModal } from "./update-claim-modal";

/**
 * Machine events offered in the Actions menu — mirrors TopMenu.machineStateActions
 * (Vue): "Abandon" events are excluded to enforce the dedicated abandon modal.
 */
export function excludeAbandonEvents(events: string[]): string[] {
  return events.filter((event) => !event.includes("Abandon"));
}

interface StaticAction {
  name: string;
  Icon: LucideIcon;
  permissions: PermissionRequirement;
  legalSteps?: ClaimLegalStepName[];
  condition?: boolean;
  onClick: () => void;
  /** A second, narrower entry on the same line (e.g. the same documents, in Box). */
  secondary?: { name: string; Icon: LucideIcon; onClick: () => void };
}

/** Port of the TopMenu.vue « Actions » dropdown (claims context). */
export function ClaimActionsMenu({ claim }: { claim: ClaimWithRelations }) {
  const { can } = useCurrentUser();
  const navigate = useNavigate();
  const { snapshot, availableEvents } = useClaimMachine(claim);

  const regenerateFirstEmail = useRegenerateFirstEmail(claim.id);
  const createOnePager = useCreateOnePager(claim.id);
  const generateFormA = useGenerateFormA(claim.id);
  const resetClaimTimeline = useResetClaimTimeline(claim.id);
  const syncReservationTickets = useSyncReservationTickets(claim.id);
  const requestClaimDocument = useRequestClaimDocument(claim.id);
  const pullRealDocuments = usePullClaimRealDocuments(claim.id);

  const [sendEvent, setSendEvent] = useState<{ open: boolean; event: string | null }>({
    open: false,
    event: null,
  });
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [showTravelAgencySignatureModal, setShowTravelAgencySignatureModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showUpdateClaimModal, setShowUpdateClaimModal] = useState(false);
  const [showBlockedFlagModal, setShowBlockedFlagModal] = useState(false);
  const [showConversationSelectionModal, setShowConversationSelectionModal] = useState(false);
  const [showInitIncidentModal, setShowInitIncidentModal] = useState(false);
  const [showBookingCertificateDialog, setShowBookingCertificateDialog] = useState(false);
  const [showMediationFormArchiveModal, setShowMediationFormArchiveModal] = useState(false);
  const [showPleadingBriefModal, setShowPleadingBriefModal] = useState(false);
  const [showStampedExhibitsModal, setShowStampedExhibitsModal] = useState(false);

  const legalStepName = claim.claimLegalStep.name;
  const machineStateActions = excludeAbandonEvents(availableEvents);

  function openFileExplorer() {
    if (isBoxFolderAvailable(claim.boxFolderId)) {
      // The current params ride along: opened from the claims list, the list underneath keeps its state.
      navigate({ to: "/claims/$claimId/files", params: { claimId: claim.id }, search: (prev) => prev });
    }
  }

  /** « Ouvrir dans Front » of TopMenu.vue (browser mode). */
  function openInFront() {
    const conversationId = claim.conversation?.frontConversationId;
    if (!conversationId) {
      notify.info("Cette claim n'est pas encore validée");
      return;
    }
    window.open(`https://app.frontapp.com/open/${conversationId}`, "_blank")?.focus();
  }

  // stateActions of TopMenu.vue.
  const stateActions: StaticAction[] = [
    {
      name: "Initialiser l'incident",
      Icon: SlidersHorizontal,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: () => setShowInitIncidentModal(true),
    },
    {
      name: "Générer le Certificat de réservation",
      Icon: Ticket,
      permissions: UPCLAIM_ROLES,
      onClick: () => setShowBookingCertificateDialog(true),
    },
    {
      name: "Générer la conversation Front",
      Icon: MessagesSquare,
      permissions: UPCLAIM_ROLES,
      legalSteps: ["UPCLAIM", "CONCILIATION", "COURT"],
      onClick: () => setShowConversationSelectionModal(true),
    },
  ];

  const staticActions: StaticAction[] = [
    {
      name: "Tous les fichiers",
      Icon: FolderOpen,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: openFileExplorer,
      secondary: { name: "Box", Icon: ExternalLink, onClick: () => openBoxFolder(claim.boxFolderId) },
    },
    {
      name: "Télécharger les pièces réelles",
      Icon: Download,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      // Staging and sandboxes only (claim puller).
      condition: config.APP_ENV !== "production",
      onClick: () => pullRealDocuments.mutate(),
    },
    {
      name: "Créer une Tâche",
      Icon: BellRing,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: () => setShowCreateTaskModal(true),
    },
    {
      name: "Modifier le dossier",
      Icon: SquarePen,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: () => setShowUpdateClaimModal(true),
    },
    {
      name: "Dossier bloqué",
      Icon: Ban,
      permissions: UPCLAIM_ROLES,
      onClick: () => setShowBlockedFlagModal(true),
    },
    {
      name: "Voir sur le Tribunal Digital",
      Icon: Scale,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["BAILIFF", "COURT", "WAITING_FOR_MONEY", "LOST"],
      onClick: () => openTribunalDigital(claim.procedure),
    },
    {
      name: "Re-générer le 1er mail",
      Icon: Mail,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["UPCLAIM"],
      condition: Boolean(claim.conversation),
      onClick: () => regenerateFirstEmail.mutate(),
    },
    {
      name: "Générer le One-Pager",
      Icon: Send,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["COURT"],
      onClick: () => createOnePager.mutate({}),
    },
    {
      name: "Générer le One-Pager mediation",
      Icon: Send,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["CONCILIATION", "COURT"],
      onClick: () => createOnePager.mutate({ isMediation: true }),
    },
    {
      name: "Générer le formulaire A",
      Icon: FileText,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["COURT"],
      condition: claim.procedure?.procedureType === "ESCP",
      onClick: () => generateFormA.mutate(),
    },
    {
      name: "Télécharger le dossier de mediation",
      Icon: FileText,
      permissions: UPCLAIM_ROLES,
      legalSteps: ["COURT"],
      onClick: () => setShowMediationFormArchiveModal(true),
    },
    {
      name: "Générer le dossier de plaidoirie",
      Icon: Files,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["COURT"],
      condition: Boolean(claim.procedure && claim.legalSubpoena),
      onClick: () => setShowPleadingBriefModal(true),
    },
    {
      name: "Générer les pièces tamponnées",
      Icon: Files,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      legalSteps: ["COURT"],
      condition: Boolean(claim.procedure),
      onClick: () => setShowStampedExhibitsModal(true),
    },
    {
      name: "Re-Signer cession agence",
      Icon: Send,
      permissions: UPCLAIM_ROLES,
      legalSteps: ["UPCLAIM", "CONCILIATION", "COURT"],
      onClick: () => setShowTravelAgencySignatureModal(true),
    },
    {
      name: "Réinitialiser la timeline",
      Icon: RotateCw,
      permissions: UPCLAIM_ROLES,
      legalSteps: ["CLAIM_VALIDATION", "CANCELLED"],
      onClick: () => resetClaimTimeline.mutate(),
    },
    {
      name: "Synchroniser les tickets",
      Icon: Ticket,
      permissions: UPCLAIM_ROLES,
      onClick: () => syncReservationTickets.mutate(claim.legalSpecification.impactedSegment.reservationId),
    },
    {
      name: "Abandonner le dossier",
      Icon: Trash2,
      permissions: UPCLAIM_ROLES,
      onClick: () => setShowAbandonModal(true),
    },
    {
      name: "Fermer ce dossier",
      Icon: ArchiveX,
      permissions: ["CUSTOMER_ADMIN", "CUSTOMER_ACCOUNT_MANAGER"],
      legalSteps: ["CLAIM_VALIDATION"],
      onClick: () => setShowAbandonModal(true),
    },
    {
      name: "Ouvrir dans Front",
      Icon: ExternalLink,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: openInFront,
    },
    {
      name: "Demande de preuve d'éligibilité",
      Icon: FileSearch,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: () => requestClaimDocument.mutate("ELIGIBILITY_PROOF"),
    },
    {
      name: "Demande de preuve transporteur",
      Icon: Plane,
      permissions: [...UPCLAIM_ROLES, "LAWYER"],
      onClick: () => requestClaimDocument.mutate("OPERATING_CARRIER"),
    },
  ];

  function isVisible(action: StaticAction): boolean {
    if (!can(action.permissions)) {
      return false;
    }
    if (action.condition === false) {
      return false;
    }
    if (action.legalSteps) {
      return action.legalSteps.includes(legalStepName);
    }
    return true;
  }

  const visibleStateActions = stateActions.filter(isVisible);
  const visibleStaticActions = staticActions.filter(isVisible);
  const canChooseNextStep = can([...UPCLAIM_ROLES, "LAWYER"]);
  // Agences : selon l'étape, aucune action ne leur est ouverte — pas de menu vide.
  const hasActions = canChooseNextStep || visibleStaticActions.length > 0;

  return (
    <>
      {hasActions ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
            Actions
            <ChevronDown className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[80vh] w-64 overflow-y-auto">
            {canChooseNextStep ? (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="border-b-2 border-gray-100 px-3 py-2 text-gray-800">
                  {machineStateActions.length ? (
                    <span className="truncate text-sm font-medium text-gray-900">
                      Choisir la prochaine étape
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-gray-900 italic">Dossier terminé</span>
                  )}
                </DropdownMenuLabel>

                {visibleStateActions.map((action) => (
                  <DropdownMenuItem
                    key={action.name}
                    onClick={action.onClick}
                    className="gap-2.5 px-3 py-2.5"
                  >
                    <action.Icon className="text-gray-400" />
                    {action.name}
                  </DropdownMenuItem>
                ))}

                {visibleStateActions.length && machineStateActions.length ? <DropdownMenuSeparator /> : null}

                {machineStateActions.map((event) => (
                  <DropdownMenuItem
                    key={event}
                    onClick={() => setSendEvent({ open: true, event })}
                    className="gap-2.5 px-3 py-2.5"
                  >
                    <ArrowRight className="text-gray-400" />
                    {event}
                  </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator />
              </DropdownMenuGroup>
            ) : null}

            {visibleStaticActions.map((action) =>
              action.secondary ? (
                // Two entries on one line: [Tous les fichiers | Box ↗], each reachable from the keyboard.
                <div key={action.name} className="flex items-stretch">
                  <DropdownMenuItem onClick={action.onClick} className="flex-1 gap-2.5 px-3 py-2.5">
                    <action.Icon className="text-gray-400" />
                    {action.name}
                  </DropdownMenuItem>
                  <span className="my-2 w-px bg-gray-200" aria-hidden="true" />
                  <DropdownMenuItem
                    onClick={action.secondary.onClick}
                    className="gap-1.5 px-3 py-2.5 text-gray-600"
                  >
                    {action.secondary.name}
                    <action.secondary.Icon className="text-gray-400" />
                  </DropdownMenuItem>
                </div>
              ) : (
                <DropdownMenuItem key={action.name} onClick={action.onClick} className="gap-2.5 px-3 py-2.5">
                  <action.Icon className="text-gray-400" />
                  {action.name}
                </DropdownMenuItem>
              ),
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <SendEventModal
        open={sendEvent.open}
        event={sendEvent.event}
        claim={claim}
        snapshot={snapshot}
        onClose={() => setSendEvent({ open: false, event: null })}
      />

      <Dialog open={showAbandonModal} onOpenChange={setShowAbandonModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Abandonner le dossier</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            <SelectAbandonType claimId={claim.id} onUpdated={() => setShowAbandonModal(false)} />
          </DialogPanel>
        </DialogContent>
      </Dialog>

      <Dialog open={showTravelAgencySignatureModal} onOpenChange={setShowTravelAgencySignatureModal}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Re-signer la cession agence</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            <TravelAgencySignatureCTA claim={claim} />
          </DialogPanel>
        </DialogContent>
      </Dialog>

      <CreateClaimTaskModal
        claimId={claim.id}
        open={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
      />

      <UpdateClaimModal
        claim={claim}
        open={showUpdateClaimModal}
        onClose={() => setShowUpdateClaimModal(false)}
      />

      <BlockedFlagModal
        claim={claim}
        open={showBlockedFlagModal}
        onClose={() => setShowBlockedFlagModal(false)}
      />

      <ConversationSelectionModal
        claimId={claim.id}
        open={showConversationSelectionModal}
        onClose={() => setShowConversationSelectionModal(false)}
      />

      <InitIncidentModal
        claimId={claim.id}
        open={showInitIncidentModal}
        onClose={() => setShowInitIncidentModal(false)}
      />

      <BookingCertificateDialog
        claimId={claim.id}
        open={showBookingCertificateDialog}
        onClose={() => setShowBookingCertificateDialog(false)}
      />

      <MediationFormArchiveModal
        claim={claim}
        open={showMediationFormArchiveModal}
        onClose={() => setShowMediationFormArchiveModal(false)}
      />

      <Dialog open={showPleadingBriefModal} onOpenChange={setShowPleadingBriefModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Générer le dossier de plaidoirie</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            <GeneratePleadingBriefCTA claim={claim} />
          </DialogPanel>
        </DialogContent>
      </Dialog>

      <Dialog open={showStampedExhibitsModal} onOpenChange={setShowStampedExhibitsModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Générer les pièces tamponnées</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            <GenerateStampedExhibitsCTA claim={claim} />
          </DialogPanel>
        </DialogContent>
      </Dialog>
    </>
  );
}
